import { ItemDataService } from '../infrastructure/items/item.data.service';
import {
    CategoryViewModelDto,
    ItemCreate,
    ItemDto,
    ItemRead,
    ItemSearchParams,
} from '../entities/item.dto';
import { ITEM_CRITICAL_FIELD_VALUE } from '../entities/item-critical-field';
import { ITEM_CRITICAL_CUSTOM_FIELD_KEY } from '../item-catalog-ui.tokens';
import { ItemStore } from '../infrastructure/items/item.store';
import { inject, Injectable } from '@angular/core';
import { RoutesForItem } from './item.routes';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { pickDefaultItemFile } from '../infrastructure/items/item-default-file.util';
import { ItemMapper } from '../infrastructure/items/item.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import { combineLatest, map, Observable, of, switchMap, tap } from 'rxjs';
import {
    ColumnSearchType,
    IBarcodeSearchFacade,
    IColumnSearchFacade,
    IDocument,
    IPagedResult,
    IQueryParams,
    SafeAny,
    SelectOption,
    TableQueryParams,
} from '@pf/shared-common';
import {
    asArraySafe,
    compareSafe,
    flattenAndMapToSelectOptions,
} from '@pf/shared-utility';
import { ResourceAuth } from '@control-tower/platform-item-catalog';
import { ItemDocumentsFacade } from './item-documents.facade';

@Injectable()
export class ItemFacade
    extends AbstractManageEntityFacade<
        ItemDto,
        ItemRead,
        ItemCreate,
        ItemSearchParams
    >
    implements IColumnSearchFacade<ItemDto>, IBarcodeSearchFacade<ItemDto>
{
    protected override dataService: ItemDataService;
    protected override store: ItemStore;
    private readonly criticalCustomFieldKey = inject(
        ITEM_CRITICAL_CUSTOM_FIELD_KEY,
        { optional: true }
    );
    private readonly itemDocumentsFacade = inject(ItemDocumentsFacade);

    nameField: keyof ItemDto = 'name';

    constructor() {
        const dataService = inject(ItemDataService);
        const routes = inject(RoutesForItem);
        const store = inject(ItemStore);
        const mapper = inject(ItemMapper);

        super(dataService, routes, store, mapper);

        this.dataService = dataService;
        this.store = store;
    }

    /** Server-side export (CSV / Excel) using current table query. */
    exportServerBlob$(
        params: IQueryParams<ItemDto>,
        exportType: string,
        filename: string,
        acceptHeader?: string
    ) {
        return this.getDefaultSearchParams$(params).pipe(
            switchMap(p =>
                this.dataService.exportServer$(
                    this.mapper.toSearchParams(p),
                    exportType,
                    filename,
                    acceptHeader
                )
            )
        );
    }

    override getDefaultSearchParams$(
        params: IQueryParams<ItemDto>
    ): Observable<IQueryParams<ItemDto>> {
        const tableParams = { ...params } as TableQueryParams<ItemDto>;
        const filters = { ...(params.filters || {}) } as SafeAny;

        if (filters.search !== undefined && filters.search !== null) {
            const trimmed = String(filters.search).trim();
            if (trimmed !== '' && filters.name == null) {
                filters.name = trimmed;
            }
            delete filters.search;
        }

        for (const key of [
            'manufacturerId',
            'assignedCategories',
            'includeDeleted',
        ]) {
            const v = filters[key];
            if (Array.isArray(v) && v.length === 0) {
                delete filters[key];
            }
        }

        const inc = filters.includeDeleted;
        if (Array.isArray(inc) && inc.length > 0) {
            filters.includeDeleted = true;
        }

        /** Advanced filter: Hazardous / Break pack / Critical (multi-select → API query fields). */
        const ATTR_INDICATORS = 'itemAttributeIndicators';
        if (filters[ATTR_INDICATORS] !== undefined) {
            const indicators = asArraySafe(filters[ATTR_INDICATORS]).map(
                String
            );
            delete filters[ATTR_INDICATORS];
            if (indicators.length === 0) {
                delete filters.hazardous;
                delete filters.isBreakPack;
                delete filters.customFieldKey;
                delete filters.customFieldValue;
            } else {
                if (indicators.includes('hazardous')) {
                    filters.hazardous = true;
                } else {
                    delete filters.hazardous;
                }
                if (indicators.includes('breakPack')) {
                    filters.isBreakPack = true;
                } else {
                    delete filters.isBreakPack;
                }
                const ck = this.criticalCustomFieldKey;
                if (indicators.includes('critical') && ck) {
                    filters.customFieldKey = ck;
                    filters.customFieldValue = ITEM_CRITICAL_FIELD_VALUE;
                } else {
                    delete filters.customFieldKey;
                    delete filters.customFieldValue;
                }
            }
        }

        tableParams.filters = filters;
        tableParams.checkboxes = {
            ...(tableParams.checkboxes || {}),
        } as SafeAny;
        if (filters.includeDeleted === true) {
            tableParams.checkboxes = {
                ...tableParams.checkboxes,
                isDeleted: true,
            } as SafeAny;
        }
        return super.getDefaultSearchParams$(
            tableParams as IQueryParams<ItemDto>
        );
    }

    getEmbeddedFilesFromStore(itemId: string): IDocument[] | null {
        const entity = this.store.get(itemId);
        const raw = entity?.files ?? null;
        if (raw == null) {
            return null;
        }
        return Array.isArray(raw) ? (raw as IDocument[]) : null;
    }

    thumbnailDoc$(
        item: ItemDto | null | undefined
    ): Observable<IDocument | null> {
        const id = item?.id;
        if (!id) {
            return of(null);
        }
        const files = item.files ?? this.getEmbeddedFilesFromStore(id) ?? null;
        if (files != null) {
            const picked = pickDefaultItemFile(files as IDocument[]);
            if (picked != null) {
                return of(picked);
            }
            if (Array.isArray(files)) {
                return of(null);
            }
        }
        return this.getDefaultItemImage$(id);
    }

    getDefaultItemImage$(itemId: string): Observable<IDocument | null> {
        const embeddedFiles = this.getEmbeddedFilesFromStore(itemId);
        if (embeddedFiles?.length) {
            const picked = pickDefaultItemFile(embeddedFiles);
            if (picked) {
                return of(picked);
            }
        }
        return this.getById$(itemId).pipe(
            switchMap(() =>
                this.inFlightRequestCache
                    .getOrCreate$(`getDefaultItemImage$:${itemId}`, () =>
                        this.itemDocumentsFacade.getItemFilesFirstPage$(itemId)
                    )
                    .pipe(map(files => pickDefaultItemFile(files)))
            )
        );
    }

    searchByBarcode$(barcodeText: string): Observable<ItemDto | null> {
        if (!barcodeText) {
            return of(null);
        }

        return this.search$({
            pageIndex: 1,
            pageSize: 1,
            filters: {
                sku: barcodeText,
            },
        }).pipe(
            map(
                results =>
                    results.data.find(item =>
                        compareSafe(item.sku, barcodeText)
                    ) ?? null
            )
        );
    }

    override applyResourceAuth(body: ItemCreate) {
        body.resourceAuth = {
            allAuthorized: true,
        } as ResourceAuth;
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as keyof ItemRead
    );

    get columnListFilterName(): string {
        return 'item';
    }

    getColumnListFilters$(): Observable<ColumnSearchType[]> {
        const queryParams = {
            pageNumber: 1,
            pageSize: 50,
        };

        return this.dataService
            .searchAll$({
                ...queryParams,
            })
            .pipe(
                map(results =>
                    results.sort((a, b) => a.name.localeCompare(b.name))
                ),
                map(result =>
                    result
                        .filter(i => !i.isDeleted)
                        .map(item => ({
                            value: item.id,
                            label:
                                (item.code ? item.code + ' - ' : '') +
                                item.name,
                        }))
                )
            );
    }

    override save$(dto: ItemDto): Observable<ItemDto> {
        const assignedCategories = [...(dto.assignedCategories || [])];
        dto.assignedCategories = undefined;
        if ('files' in dto) {
            delete (dto as SafeAny).files;
        }
        if ('warehouseAmounts' in dto) {
            delete (dto as SafeAny).warehouseAmounts;
        }
        return super.save$(dto).pipe(
            switchMap(item => {
                return this.updateItemCategories(
                    item.id,
                    assignedCategories || []
                ).pipe(
                    tap(this.store.update.bind(this.store)),
                    map(entity => {
                        item.assignedCategories = (entity.assignedCategories ||
                            []) as CategoryViewModelDto[];
                        return item;
                    })
                );
            })
        );
    }

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof ItemDto, string>> {
        return { name: searchText };
    }

    private updateItemCategories(
        id: string,
        assignedCategories: CategoryViewModelDto[]
    ) {
        const entity = this.store.get(id);
        const existingCategories = entity.assignedCategories || [];
        const categoriesToDelete = assignedCategories.filter(c => c.isDeleted);
        const categoriesToAdd = assignedCategories.filter(
            c => !c.isDeleted && existingCategories.every(ac => ac.id !== c.id)
        );

        const operations = [];
        if (categoriesToDelete.length > 0) {
            operations.push(
                ...this.dataService.deleteItemCategories$(
                    id,
                    categoriesToDelete
                )
            );
        }
        if (categoriesToAdd.length > 0) {
            operations.push(
                ...this.dataService.addItemCategories$(id, categoriesToAdd)
            );
        }
        return operations.length > 0
            ? combineLatest(operations).pipe(
                  map(() => {
                      entity.assignedCategories = assignedCategories.filter(
                          c => !c.isDeleted
                      );
                      return entity;
                  })
              )
            : of(entity);
    }

    override mapToSelectOptions(
        source: Observable<IPagedResult<ItemDto>>
    ): Observable<SelectOption[]> {
        return flattenAndMapToSelectOptions<ItemDto>(
            item => item.name + (item.code ? ' - ' + item.code : ''),
            'id',
            item =>
                item.assignedCategories?.map(x => x.name) ?? ['Uncategorized']
        )(source);
    }
}
